import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Compass, MessageCircle } from "lucide-react";
import { Reveal } from "./Motion";
import { AfterState } from "./AfterState";
import { SAMPLE_LESSON as S } from "../data/sample";

const ICONS = [Search, Compass, MessageCircle];

export const SampleLesson = ({ eyebrow = "See one lesson in action", title = "This is what a lesson actually asks you." }) => (
  <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="sample-lesson">
    <div className="grid lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-4 lg:sticky lg:top-28">
        <Reveal>
          <p className="label text-sage mb-4">{eyebrow}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-navy-deep leading-tight">{title}</h2>
          <p className="mt-6 text-lg text-navy-deep/70 leading-relaxed">
            A real question set from Lesson 1 — the same three questions every lesson walks you through, in this order.
          </p>
          <p className="mt-4 text-navy-deep/70 leading-relaxed" data-testid="sample-after">{S.after}</p>
          <Link to="/learn/l1" data-testid="sample-lesson-cta" className="mt-8 inline-flex items-center gap-3 rounded-full bg-gold text-navy-deep px-7 py-3.5 font-semibold hover:bg-navy hover:text-cream transition-colors">
            {S.cta} <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="lg:col-span-8">
        <div className="rounded-[2rem] bg-navy text-cream overflow-hidden shadow-[0_30px_80px_rgba(13,27,42,0.25)]" data-testid="sample-lesson-screen">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-cream/10 bg-navy-deep/40">
            <span className="w-2.5 h-2.5 rounded-full bg-cream/20" /><span className="w-2.5 h-2.5 rounded-full bg-cream/20" /><span className="w-2.5 h-2.5 rounded-full bg-gold/70" />
            <span className="ml-4 text-xs tracking-wider uppercase text-cream/50">officiummentale.org/learn/l1</span>
          </div>
          <div className="p-7 md:p-10">
            <p className="label text-gold">{S.kicker}</p>
            <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">{S.title}</h3>
            <p className="text-cream/60 mt-1">{S.subtitle}</p>

            <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-6">
              <p className="label text-gold/80 mb-2">The lesson's core question</p>
              <p className="font-serif italic text-xl md:text-2xl leading-snug" data-testid="sample-core-question">&ldquo;{S.coreQuestion}&rdquo;</p>
            </div>

            <ol className="mt-6 grid md:grid-cols-3 gap-3">
              {S.steps.map((st, i) => {
                const Icon = ICONS[i];
                return (
                  <li key={st.step} className={`rounded-2xl p-5 border ${i === 2 ? "bg-cream text-navy-deep border-cream" : "bg-cream/5 border-cream/10"}`} data-testid={`sample-step-${st.step.toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center ${i === 2 ? "bg-navy text-gold" : "bg-gold/20 text-gold"}`}><Icon size={15} /></span>
                      <span className="font-serif text-lg">{String(i + 1).padStart(2, "0")} {st.step}</span>
                    </div>
                    <p className={`label mt-3 ${i === 2 ? "text-navy/60" : "text-cream/50"}`}>{st.label}</p>
                    <p className={`mt-2 leading-relaxed ${i === 2 ? "text-navy-deep/85" : "text-cream/85"}`}>{st.q}</p>
                  </li>
                );
              })}
            </ol>

            <div className="mt-6 grid md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-cream/5 border border-cream/10 p-5">
                <p className="label text-cream/50 mb-2">What to look for</p>
                <p className="text-cream/80 leading-relaxed">{S.lookFor}</p>
              </div>
              <div className="rounded-xl bg-cream/5 border border-cream/10 p-5">
                <p className="label text-cream/50 mb-2">What you carry to your provider</p>
                <p className="text-cream/80 leading-relaxed" data-testid="sample-carry">{S.carry}</p>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-8 label text-navy/50">What it looks like when you're done</p>
        <AfterState />
      </Reveal>
    </div>
  </section>
);
